import type { ReactNode } from "react";

export type ToastVariant = "success" | "error" | "info" | "warning" | "loading";

export interface ToastAction {
  label: string;
  /** Rückgabe `false` verhindert das automatische Schließen nach Klick. */
  onClick: () => void | boolean | Promise<void>;
}

export interface ToastInput {
  /** Eigene ID zum Aktualisieren/Ersetzen eines bestehenden Toasts. */
  id?: string;
  variant?: ToastVariant;
  title?: ReactNode;
  message?: ReactNode;
  /** Anzeigedauer in ms. `0` = bleibt offen, bis manuell geschlossen. */
  duration?: number;
  action?: ToastAction;
  dismissible?: boolean;
}

export interface Toast {
  id: string;
  variant: ToastVariant;
  title?: ReactNode;
  message?: ReactNode;
  duration: number;
  action?: ToastAction;
  dismissible: boolean;
  createdAt: number;
}

const MAX_VISIBLE = 5;

const DEFAULT_DURATIONS: Record<ToastVariant, number> = {
  success: 4000,
  info: 4500,
  warning: 6000,
  error: 7000,
  loading: 0,
};

type Listener = (toasts: Toast[]) => void;

let toasts: Toast[] = [];
const listeners = new Set<Listener>();
let counter = 0;

function emit() {
  for (const listener of listeners) listener(toasts);
}

export function subscribeToasts(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getToastsSnapshot(): Toast[] {
  return toasts;
}

function nextId(): string {
  counter += 1;
  return `toast-${counter}-${Date.now().toString(36)}`;
}

export function upsertToast(input: ToastInput): string {
  const variant = input.variant ?? "info";
  const duration = input.duration ?? DEFAULT_DURATIONS[variant];
  const id = input.id ?? nextId();

  const prev = toasts.find((t) => t.id === id);
  if (prev) {
    const next: Toast = {
      ...prev,
      variant,
      title: input.title ?? prev.title,
      message: input.message ?? prev.message,
      duration,
      action: input.action ?? prev.action,
      dismissible: input.dismissible ?? prev.dismissible,
    };
    toasts = toasts.map((t) => (t.id === id ? next : t));
    emit();
    return id;
  }

  const toast: Toast = {
    id,
    variant,
    title: input.title,
    message: input.message,
    duration,
    action: input.action,
    dismissible: input.dismissible ?? true,
    createdAt: Date.now(),
  };

  toasts = [...toasts, toast];
  if (toasts.length > MAX_VISIBLE) {
    toasts = toasts.slice(toasts.length - MAX_VISIBLE);
  }
  emit();
  return id;
}

export function dismissToast(id?: string) {
  if (id == null) {
    if (toasts.length === 0) return;
    toasts = [];
  } else {
    if (!toasts.some((t) => t.id === id)) return;
    toasts = toasts.filter((t) => t.id !== id);
  }
  emit();
}
